import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Stack, useMediaQuery } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useUnitProvider from '/@/core/context/unit';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import Title from '/@/v3/components/title';

const GDUDialog = ({ open, onClose }) => {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { open: openGdu, toggle: toggleGdu } = useToggle(true);

    const reference = [
        {
            label: _("Rojo: 0-2 GDU's"),
            for7Days: '0-14',
            for14Days: '0-28',
            color: '#FF2929',
        },
        {
            label: _("Naranja: 2-5 GDU's"),
            for7Days: '14-35',
            for14Days: '28-70',
            color: '#EF9E00',
        },
        {
            label: _("Amarillo: 5-10 GDU's"),
            for7Days: '35-70',
            for14Days: '70-140',
            color: '#FBFF25',
        },
        {
            label: _("Verde: 10+ GDU's"),
            for7Days: '> 70',
            for14Days: '> 140',
            color: '#8AE01C',
        },
    ];

    const units = useUnitProvider();
    const baseTemp = units.toTemperature(10, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{ m: 6 }}
            PaperProps={{
                sx: {
                    width: '100%',
                    m: { xs: 0, md: 1 },
                },
            }}
        >
            <DialogTitle disableTypography sx={{ p: 0, textAlign: 'end' }}>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 2, md: 4 } }} onClose={onClose}>
                <Stack spacing={1}>
                    <Title size='lg' bolder>
                        <Box sx={[{ display: 'flex', gap: 2, p: 0, alignItems: 'center' }]}>
                            <span>{_('Que son los GDUs?')}</span>
                            <IconButton onClick={toggleGdu}>
                                {openGdu ? (
                                    <KeyboardArrowDownIcon fontSize='large' />
                                ) : (
                                    <KeyboardArrowUpIcon fontSize='large' />
                                )}
                            </IconButton>
                        </Box>
                    </Title>
                    <Collapse in={openGdu} unmountOnExit={true}>
                        <Stack spacing={1}>
                            <Title size='sm'>
                                {_(
                                    'Es el tiempo térmico o sumatoria de grados días, que es usado para describir el desarrollo de los cultivos.'
                                )}
                            </Title>
                            <Title size='sm'>
                                {_(
                                    'El cálculo de Grados-día de crecimiento o tiempo térmico utiliza la siguiente fórmula:'
                                )}
                            </Title>
                            <Title size='sm' bold>
                                {_(`GDU = (Tmín. + Tmáx.)/2 – Temperatura base ${baseTemp} ${units.temperature}`)}
                            </Title>
                        </Stack>
                    </Collapse>
                    <Title size='lg' bolder>
                        <Box sx={[{ display: 'flex', gap: 2, p: 0, alignItems: 'center' }]}>
                            <span>{_('Riesgo de emergencia en función de las temperaturas')}</span>
                            <IconButton onClick={toggleGdu}>
                                {!openGdu ? (
                                    <KeyboardArrowDownIcon fontSize='large' />
                                ) : (
                                    <KeyboardArrowUpIcon fontSize='large' />
                                )}
                            </IconButton>
                        </Box>
                    </Title>
                    <Collapse in={!openGdu} unmountOnExit={true}>
                        <Stack spacing={1}>
                            <Title size='sm'>
                                {_(
                                    'En la escala de Forecast de GDU a 7 y 14 días, los GDU´s estimados diarios se suman para estos dos períodos considerados. Es una escala para '
                                )}{' '}
                                <span style={{ fontWeight: 'bold' }}>{_('tomar como referencia')}</span>
                                {_(' para entender el riesgo de emergencia en función de las temperaturas.')}
                            </Title>
                            <Title size='sm'>
                                {_(
                                    'Mostrando un pronóstico de las condiciones de emergencia para las siembras tempranas en la región templada maicera, en función de las  '
                                )}{' '}
                                <span style={{ fontWeight: 'bold' }}>{_('temperaturas del aire')}</span>
                                {_(' máximas y mínimas diarias registradas desde TWC (The Weather Company).')}
                            </Title>
                            <Title size='sm'>
                                {_(
                                    'Como valor de referencia, en líneas generales, el cultivo de maíz requiere de 100 a 120 GDD (grados días de desarrollo o suma térmica) para emerger, dependiendo de la acumulación de grados días la duración del período desde la siembra a la emergencia. En la escala, para 7 días de forecast, valores mayores a 70 GDU´s acumulados, significa emergencia para maíz'
                                )}{' '}
                                <span style={{ fontWeight: 'bold' }}>{_('sin riesgo.')}</span>
                            </Title>
                        </Stack>
                    </Collapse>
                    {!isMobile && (
                        <TableContainer>
                            <Table
                                sx={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}
                                aria-label='simple table'
                            >
                                <TableHead sx={{ '& tr th': { border: 0 } }}>
                                    <TableRow>
                                        <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>
                                            {_('Definicón de los umbrales:')}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>{_('en 7 días')}</TableCell>
                                        <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>{_('en 14 días')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reference.map((row) => (
                                        <TableRow
                                            sx={{
                                                fontSize: 14,
                                                td: {
                                                    border: '1px solid #E1E1E1',
                                                    borderLeft: 0,
                                                    borderRight: 0,
                                                },
                                                'td:first-child': {
                                                    borderLeft: '1px solid #E1E1E1',
                                                    borderTopLeftRadius: '8px',
                                                    borderBottomLeftRadius: '8px',
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                    backgroundColor: row.color,
                                                },
                                                'td:last-child': {
                                                    borderLeft: 0,
                                                    borderRight: '1px solid #E1E1E1',
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    borderTopRightRadius: '8px',
                                                    borderBottomRightRadius: '8px',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ p: 2 }}>{row.label}</TableCell>
                                            <TableCell sx={{ p: 2 }}>{row.for7Days}</TableCell>
                                            <TableCell sx={{ p: 2 }}>{row.for14Days}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default GDUDialog;
